import React from "react";
import { profile, education } from "@/lib/data";

const ABOUT_IMG =
    "https://images.unsplash.com/photo-1753715613434-9c7cb58876b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9kZXJuJTIwZGV2ZWxvcGVyJTIwd29ya3NwYWNlfGVufDB8fHx8MTc4MTI5Mzk1NXww&ixlib=rb-4.1.0&q=85";

export default function About() {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative py-24 md:py-32 border-t border-term-green/15"
        >
            <SectionHeader index="01" label="about" command="cat ./about.md" />

            <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 grid lg:grid-cols-12 gap-10 lg:gap-14">
                {/* Image with grid overlay */}
                <div className="lg:col-span-5 relative">
                    <div className="relative aspect-[4/5] border border-term-green/30 overflow-hidden bg-bg-surface">
                        <img
                            src={ABOUT_IMG}
                            alt="Developer workspace"
                            loading="lazy"
                            className="w-full h-full object-cover opacity-70 grayscale contrast-110"
                        />
                        <div className="absolute inset-0 terminal-grid-fine opacity-80 pointer-events-none mix-blend-overlay" />
                        <div className="absolute inset-0 bg-gradient-to-t from-bg-base via-transparent to-transparent pointer-events-none" />
                        <div className="absolute top-3 left-3 right-3 flex items-center justify-between font-mono text-[10px] text-term-green uppercase tracking-[0.22em] pointer-events-none">
                            <span>~/about.jpg</span>
                            <span>1080×1350</span>
                        </div>
                        <div className="absolute bottom-3 left-3 right-3 font-mono text-[10px] text-ink-tertiary uppercase tracking-[0.22em] pointer-events-none">
                            <span className="text-term-green">●</span> rendered · 60fps
                        </div>
                    </div>
                </div>

                {/* Text */}
                <div className="lg:col-span-7 space-y-6">
                    <h2 className="font-mono text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-ink-primary leading-tight">
                        Delivering cloud-native microservices & GenAI platform solutions.
                        <span className="text-term-green">~/</span>building systems
                        that <span className="text-term-green">scale</span>{" "}
                        gracefully.
                    </h2>

                    <p className="font-display text-base md:text-lg text-ink-secondary leading-relaxed">
                        {profile.summary}
                    </p>

                    <p className="font-display text-base md:text-lg text-ink-secondary leading-relaxed">
                        {profile.aboutShort}
                    </p>

                    <div className="pt-4 border-t border-term-green/15 grid sm:grid-cols-2 gap-6 font-mono text-sm">
                        <div data-testid="about-education">
                            <div className="text-[10.5px] uppercase tracking-[0.22em] text-term-green mb-2">
                                // education
                            </div>
                            <div className="text-ink-primary">{education.degree}</div>
                            <div className="text-ink-secondary text-xs mt-1">
                                {education.school}
                            </div>
                            <div className="text-ink-tertiary text-xs mt-0.5">
                                {education.location} · {education.period}
                            </div>
                        </div>
                        <div data-testid="about-contact-quick">
                            <div className="text-[10.5px] uppercase tracking-[0.22em] text-term-green mb-2">
                                // contact
                            </div>
                            <a
                                href={`mailto:${profile.email}`}
                                className="block text-ink-primary hover:text-term-green transition-colors break-all"
                            >
                                {profile.email}
                            </a>
                            <a
                                href={profile.socials.website}
                                target="_blank"
                                rel="noreferrer"
                                className="block text-ink-secondary hover:text-term-green text-xs mt-1 transition-colors"
                            >
                                {profile.socials.website.replace(/^https?:\/\//, "")}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function SectionHeader({ index, label, command }) {
    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between gap-6 border-b border-term-green/20 pb-4">
                <div className="flex items-baseline gap-4">
                    <span className="font-mono text-xs text-ink-tertiary tabular-nums">
                        {index}
                    </span>
                    <h3 className="font-mono text-[11px] uppercase tracking-[0.32em] text-term-green">
                        // {label}
                    </h3>
                </div>
                <span className="hidden md:inline-block font-mono text-[11px] text-ink-tertiary uppercase tracking-[0.22em]">
                    <span className="text-term-green">$</span> {command}
                </span>
            </div>
        </div>
    );
}
